/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, SyntheticEvent, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { Link as RouterLink } from 'react-router-dom';
import { Grid, Link, makeStyles, Typography } from '@material-ui/core';

import { getExclusionList } from './apiQueries';
import { createOrUpdateExclusionList } from './apiMutations';
import { getLatestHighlight } from '../airings/apiQueries';

import ClipSelector from './ClipSelector';
import ExclusionPeriods from './ExclusionPeriods';

import EditorForm from '../common/EditorForm';
import Dialog from '../common/Dialog';

import { getVideoUrl, compareStartTime } from './utils';
import { Period, Highlight, Channel, Game, ExclusionPeriod, ExclusionPeriodConnection, SimpleAsset, HLS_TRANSPORT, DRMTYPE } from '../../utils/types';
import { useAlertContext } from '../../context/alertContext';
import { convertExclusionListToExclusionListInput } from '../../utils/inputTransform';
import { useLicenseAcquisitionUrl } from '../../hooks';

const getHighlightTypeBySport = (sport: string | null | undefined): string => {
	return sport?.toLowerCase() === 'f1' ? 'DRAFT' : 'LIVE';
};

const useStyles = makeStyles((theme) => ({
	actionLink: {
		color: theme.palette.secondary.main,
		display: 'flex',
		marginBottom: theme.spacing(2)
	}
}));

const Exclusions: FC<ExclusionsProps> = ({ channel, drm, game, id, pageType, sources }) => {
	const classes = useStyles();
	const { setAlert } = useAlertContext();

	const [dialogOpen, setDialogOpen] = useState(false);
	const [saveDisabled, setSaveDisabled] = useState(true);
	const [updateExclusionList] = useMutation(createOrUpdateExclusionList);

	const [exclusionPeriods, setExclusionPeriods] = useState<ExclusionPeriodConnection>();
	const [highlight, setHighlight] = useState<Highlight | null>(null);
	const [selectedExclusionPeriods, setSelectedExclusionPeriods] = useState<ExclusionPeriodConnection>();

	const { loading, error, data } = useQuery(getExclusionList, {
		variables: {
			airingId: decodeURIComponent(id),
			fetchPolicy: 'no-cache'
		}
	});

	const queryCommon = {
		type: getHighlightTypeBySport(game?.sport),
		available: true,
		fetchPolicy: 'no-cache'
	};

	const { data: latestHighlightData } = useQuery(getLatestHighlight, {
		variables: pageType === 'game' ? { gameId: decodeURIComponent(id), ...queryCommon } : { airingId: decodeURIComponent(id), ...queryCommon }
	});

	const airingVideoUrl = getVideoUrl(channel?.sourceAssets);

	const drmHlsSource = sources?.find(({ transport }): boolean => transport === HLS_TRANSPORT);
	const { laUrl: drmLaUrl } = useLicenseAcquisitionUrl(
		drmHlsSource
			? {
					configAlias: drmHlsSource.configAlias,
					contentRef: drmHlsSource.contentRef,
					propositionContentId: drmHlsSource.propositionContentId,
					protection: drmHlsSource.protection,
					drmProvider: drmHlsSource.drmProvider
				}
			: undefined
	);

	useEffect(() => {
		if (data?.getExclusionList?.periods)
			setExclusionPeriods({
				items: data?.getExclusionList?.periods.items.sort(compareStartTime)
			});
	}, [data]);

	useEffect(() => {
		if (latestHighlightData?.getLatestHighlight) {
			setHighlight(latestHighlightData.getLatestHighlight);
		}
	}, [latestHighlightData]);

	const showDialog = (): void => {
		setDialogOpen(true);
	};

	const hideDialog = (): void => {
		setDialogOpen(false);
	};

	const handleExclusionPeriodRemove = (period: ExclusionPeriod): Promise<unknown> => {
		const newPeriods: ExclusionPeriod[] = exclusionPeriods?.items?.length ? [...exclusionPeriods?.items] : [];
		const index = newPeriods.indexOf(period);
		newPeriods.splice(index, 1);

		setExclusionPeriods({ items: newPeriods });
		setSaveDisabled(false);
		return Promise.resolve();
	};

	const clearAll = (): void => {
		setExclusionPeriods({ items: [] });
		setSaveDisabled(false);
	};

	const handleSelectionChange = (periods: Period[]): void => {
		const newExclusions: ExclusionPeriodConnection = {
			items: null
		};

		if (periods.length) {
			newExclusions.items = periods.map(
				(period: Period): ExclusionPeriod => ({
					id: period?.id || '',
					playId: period?.playId || '',
					title: period?.title || '',
					shortText: period?.shortText || '',
					startTime: period?.source?.startTime || '',
					endTime: period?.source?.endTime || ''
				})
			);
		}

		setSelectedExclusionPeriods(newExclusions);
	};

	const handleNewExclusionPeriodsAdd = (): Promise<unknown> => {
		setSaveDisabled(false);

		const existing: ExclusionPeriod[] = exclusionPeriods?.items?.length ? [...exclusionPeriods?.items] : [];
		const existingIds = existing.map((item) => item.id).filter((value, index, self) => self.indexOf(value) === index);
		const selected: ExclusionPeriod[] = selectedExclusionPeriods?.items?.length ? [...selectedExclusionPeriods?.items] : [];
		const merged: ExclusionPeriod[] = [...existing, ...selected.filter((p: ExclusionPeriod) => !existingIds.includes(p.id))];
		if (merged.length) {
			setExclusionPeriods({
				items: merged.sort(compareStartTime)
			});
		}
		setSelectedExclusionPeriods(undefined);
		hideDialog();
		return Promise.resolve();
	};

	const handleSelectionCancel = (): void => {
		setSelectedExclusionPeriods(undefined);
		hideDialog();
	};

	const handleSave = async (event: SyntheticEvent): Promise<void> => {
		event.stopPropagation();
		event.preventDefault();

		if (window.confirm('Are you sure you want to save these changes?')) {
			try {
				setSaveDisabled(true);
				const exclusionListInput = convertExclusionListToExclusionListInput({
					airingId: id,
					periods: exclusionPeriods,
					gameId: game?.id
				});
				const response = await updateExclusionList({
					variables: {
						exclusionList: exclusionListInput
					}
				});
				setAlert(response?.data?.createOrUpdateExclusionList?.message || 'Excluded Clips updated');
			} catch {
				setAlert('Error updating excluded clips');
				setSaveDisabled(false);
			}
		}
		return Promise.resolve();
	};

	if (loading) return <Typography>Loading...</Typography>;
	if (error) return <Typography>Error loading data from the server</Typography>;

	const renderClipSelector = (): JSX.Element => {
		if (drm && drmHlsSource && drmLaUrl) {
			const { protection: drmType, url: source } = drmHlsSource;

			return (
				<ClipSelector
					drmType={drmType.toLowerCase() as DRMTYPE}
					laSource={drmLaUrl}
					onChange={handleSelectionChange}
					periods={highlight?.periods?.items || []}
					videoUrl={source}
				/>
			);
		} else if (drm && drmHlsSource && !drmLaUrl) {
			return <p>DRM license aquisition in progress</p>;
		} else if ((!drm || !drmHlsSource) && airingVideoUrl) {
			return <ClipSelector onChange={handleSelectionChange} periods={highlight?.periods?.items || []} videoUrl={airingVideoUrl} />;
		} else {
			return <p>Video URL and Start Time could not be found</p>;
		}
	};

	const renderExclusionPeriods = (): JSX.Element => {
		if (drm && drmHlsSource && drmLaUrl) {
			const { protection: drmType, url: source } = drmHlsSource;

			return (
				<ExclusionPeriods
					drmType={drmType.toLowerCase() as DRMTYPE}
					items={exclusionPeriods?.items || []}
					laSource={drmLaUrl}
					onRemove={handleExclusionPeriodRemove}
					onRemoveAll={clearAll}
					onShowOptions={showDialog}
					videoUrl={source}
				/>
			);
		} else {
			return (
				<ExclusionPeriods
					items={exclusionPeriods?.items || []}
					onRemove={handleExclusionPeriodRemove}
					onRemoveAll={clearAll}
					onShowOptions={showDialog}
					videoUrl={airingVideoUrl}
				/>
			);
		}
	};

	return (
		<>
			<Dialog title='Latest Highlight Clips' open={dialogOpen} onOK={handleNewExclusionPeriodsAdd} onCancel={handleSelectionCancel}>
				{renderClipSelector()}
			</Dialog>
			<EditorForm onSubmit={handleSave} submitText='Save Changes' saveDisabled={saveDisabled}>
				<>
					<Grid spacing={1} container>
						{pageType === 'game' ? (
							<Grid item>
								<Link component={RouterLink as any} to={`/games/${id}`} className={classes.actionLink}>
									Game: {id}
								</Link>
							</Grid>
						) : (
							<>
								<Grid item>
									<Link component={RouterLink as any} to={`/airings/${id}`} className={classes.actionLink}>
										Airing: {id}
									</Link>
								</Grid>
								<Grid item>
									<Typography variant='body2'>
										{channel?.name} ({channel?.territory})
									</Typography>
								</Grid>
							</>
						)}
					</Grid>
					{renderExclusionPeriods()}
				</>
			</EditorForm>
		</>
	);
};

type ExclusionsProps = {
	channel: Channel | null | undefined;
	drm?: boolean;
	game: Game | null | undefined;
	id: string;
	pageType: 'airing' | 'game' | null | undefined;
	sources?: SimpleAsset[];
};

export default Exclusions;
